import React from "react";
import AppBar from "../components/atoms/appbar";
import Tabs, { useTabs } from "../components/atoms/tab";
import { withTabContnet } from "../components/atoms/tab/withTabContent";
import TermsScreen from "../components/layout/signup/TermsScreen";
import Button from "../components/atoms/button";
import EnterInfoScreen from "../components/layout/signup/EnterInfoScreen";
import PreferredRegionScreen from "../components/layout/signup/PreferredRegionScreen";
import PageCount from "../components/atoms/count/PageCount";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import type { MyInfo, SignUpInput } from "../types/auth.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { signupSchema } from "../lib/schemas/auth.schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { JoinConfirmOTPRequest } from "../@apis/dto/request/auth.request";
import { joinConfirmOtp, getMyInfo } from "../@apis/v1/auth.service";
import { useJoinTermsList } from "../@apis/hooks/useAuth";
import { useAccessStore } from "../lib/store/useAccessStore";
import { useMyInfoStore } from "../lib/store/useMyInfoStore";
import { flushSync } from "react-dom";
import { setGlobalAuthHeader } from "../@apis/axios.config";
import { resetSessionQueries, seedMyInfoQuery } from "../@apis/queryClient";
import type {
  AccessResponse,
  AuthCertificationResponse,
} from "../@apis/dto/response/auth.response";
import type { OnErrorResponse } from "../@apis/dto/response/common.response";
import { useModalStore } from "../lib/store/useModalStore";
import { requestPushTokenRegistration } from "../lib/utils/boot";
import { useKeyboardSafeScroll } from "../hooks/useKeyboardSafeScroll";

export default function Signup() {
  const { setAccess } = useAccessStore();
  const { setMyInfo } = useMyInfoStore();
  const { openModal } = useModalStore();

  const navigate = useNavigate();
  const pageRef = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const { currentTab, next, prev, move } = useTabs(1);

  useKeyboardSafeScroll(pageRef, footerRef);
  const TermsScreenTab = React.useMemo(
    () => withTabContnet(TermsScreen, {}),
    [],
  );
  const EnterInfoScreenTab = React.useMemo(
    () => withTabContnet(EnterInfoScreen, {}),
    [],
  );
  const PreferredRegionScreenTab = React.useMemo(
    () => withTabContnet(PreferredRegionScreen, {}),
    [],
  );
  const initialCertification: AuthCertificationResponse = {
    requestNo: "",
    resUniqId: "",
    tokenVersionId: "",
    siteCode: "",
    reqDtim: "",
    tokenVal: "",
    reqNo: "",
  };

  const [certification, setCertification] =
    React.useState<AuthCertificationResponse>(initialCertification);
  // 본인인증(OTP) 요청 완료 여부
  const [otpRequested, setOtpRequested] = React.useState(false);
  // alert 확인 시 이동/강조할 입력란 (tab 2)
  const [focusTarget, setFocusTarget] = React.useState<string | null>(null);
  const [pendingAccess, setPendingAccess] =
    React.useState<AccessResponse | null>(null);

  const handleCertification = (item: AuthCertificationResponse) => {
    setCertification(item);
    setOtpRequested(true);
  };

  const methods = useForm<SignUpInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      birthdate: "",
      sidoCode: "",
      sidoName: "",
      sigunguCode: "",
      sigunguName: "",
    },
  });
  const { watch, resetField } = methods;
  const values = watch();

  const resetEnterInfo = () => {
    (
      ["name", "birthdate", "jumin1", "mobileco", "mobileno", "otp"] as const
    ).forEach((field) => resetField(field));
    setCertification(initialCertification);
    setOtpRequested(false);
    setFocusTarget(null);
  };

  const handlePrev = () => {
    if (currentTab === 2 || currentTab === 3) {
      // tab 2(본인인증 입력) 화면 값 초기화
      resetEnterInfo();
    }
    prev();
  };

  // 가입 완료 → 로그인 처리(토큰 적용/푸시 등록) 후 홈으로 이동
  const goHome = async () => {
    if (pendingAccess) {
      flushSync(() => {
        setGlobalAuthHeader(pendingAccess.accessToken);
        setAccess({
          ...pendingAccess,
          expiresAt: Math.floor(Date.now() / 1000) + 10 * 60,
        });
      });
      resetSessionQueries();
      const myInfoRes = await getMyInfo();
      if (myInfoRes.success) {
        setMyInfo(myInfoRes.data as MyInfo);
        seedMyInfoQuery(myInfoRes);
      }
      requestPushTokenRegistration({ force: true });
    }
    navigate("/", { replace: true });
  };

  const { data: termsData } = useQuery({ ...useJoinTermsList() });

  const canNext = async (step: number) => {
    if (step === 1) {
      const termsList = termsData?.data?.termsList ?? [];
      const requiredOk = termsList
        .filter((term) => term.isRequired)
        .every((term) =>
          (values.joinTermsAgreeList ?? []).some(
            (agree) => agree.termsId === term.id && agree.isAgreed,
          ),
        );
      if (!requiredOk) {
        openModal("ALERT_DIALOG", {
          title: "알림",
          content: "필수 약관을 모두 선택해 주세요.",
        });
        return false;
      }
      return true;
    }
    return false;
  };

  // tab 2 필수 입력 검증: 화면 상단부터 순서대로 첫 번째 미입력 항목을 반환
  const getMissingField = (): { message: string; field: string } | null => {
    const [name, birthdate, mobileno, otp] = watch([
      "name",
      "birthdate",
      "mobileno",
      "otp",
    ]);

    if (!name) return { message: "이름을 입력해 주세요.", field: "name" };
    if ((birthdate?.length ?? 0) !== 6)
      return { message: "생년월일을 입력해 주세요.", field: "birthdate" };
    if ((mobileno?.length ?? 0) < 10)
      return { message: "휴대전화번호를 입력해 주세요.", field: "mobileno" };
    if (!otpRequested)
      return { message: "본인인증을 진행해 주세요.", field: "certification" };
    if ((otp?.length ?? 0) !== 6)
      return { message: "인증번호를 입력해 주세요.", field: "otp" };

    return null;
  };

  const handleNext = async () => {
    if (currentTab === 1) {
      if (await canNext(1)) next();
      return;
    }

    if (currentTab === 2) {
      const missing = getMissingField();
      if (missing) {
        openModal("ALERT_DIALOG", {
          title: "알림",
          content: missing.message,
          // 확인 클릭 시 해당 입력란으로 스크롤 이동 및 강조
          onConfirm: () => setFocusTarget(missing.field),
        });
        return;
      }
      // 본인인증 정보 입력 완료 → 가입 진행 (선호지역 제외)
      confirmOTPMutation.mutate({
        ...certification,
        otp: values.otp,
        joinTermsAgreeList: values.joinTermsAgreeList,
      });
      return;
    }

    if (currentTab === 3) {
      // 선호지역은 셀렉트 변경 시점에 PreferredRegionScreen에서 저장되므로
      // 여기서는 홈으로 이동만 처리
      goHome();
    }
  };

  // 인증번호 불일치 등 인증 실패 처리: alert 확인 시 인증번호 입력란으로
  // 스크롤 이동 및 강조하고, 기존 입력한 인증번호는 초기화한다.
  const handleOtpFail = (content: string) => {
    openModal("ALERT_DIALOG", {
      title: "인증 실패",
      content: content,
      onConfirm: () => {
        // 가입(confirm-otp)은 본인인증 탭에서 진행되므로 인증번호 입력란으로 복귀
        resetField("otp");
        setFocusTarget("otp");
      },
    });
  };

  const confirmOTPMutation = useMutation({
    mutationFn: (form: JoinConfirmOTPRequest) => joinConfirmOtp(form),
    onSuccess: (res) => {
      const { success, data } = res;
      if (success) {
        setMyInfo({
          name: values.name,
          birthdate: values.birthdate,
          mobileno: values.mobileno,
        });
        // 가입 직후 선호지역 저장 API 인증을 위해 토큰을 적용
        // (전역 인증 상태는 완료 화면에서 확정하므로 axios 헤더만 설정)
        setGlobalAuthHeader(data.accessToken);
        setPendingAccess(data);
        // 선호지역 선택 단계로 이동
        move(3);
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      handleOtpFail(
        error?.code === "S010"
          ? "인증번호가 일치하지 않습니다.\n인증번호 확인 후 다시 입력해 주세요."
          : error?.message || "요청에 실패하였습니다.",
      );
    },
  });

  return (
    <div ref={pageRef} className="flex flex-col">
      <AppBar title="회원가입" backButton />

      <section className="px-[16px] flex flex-col justify-between min-h-[calc(100vh-56px)] space-y-2 [@media(pointer:coarse)]:pb-[34vh]">
        <PageCount
          className="mb-[30px]"
          totalCount={3}
          currentPage={currentTab as number}
        />
        <div className="flex-1">
          <FormProvider {...methods}>
            <Tabs currentTab={currentTab}>
              <TermsScreenTab />
              <EnterInfoScreenTab
                onCertification={handleCertification}
                otpRequested={otpRequested}
                focusTarget={focusTarget}
                onFocusHandled={() => setFocusTarget(null)}
              />
              <PreferredRegionScreenTab />
            </Tabs>
          </FormProvider>
        </div>

        <div
          ref={footerRef}
          className="flex-row w-full gap-[10px] sticky bottom-0 left-0 z-50 bg-white pb-[16px] pt-[10px]"
        >
          {/* 가입 완료(tab3) 이후에는 정보 수정 불가하므로 이전 버튼 미노출 */}
          {currentTab === 2 && (
            <Button
              className="text-sm flex-1"
              variant={"white"}
              onClick={handlePrev}
              label="이전"
            />
          )}
          <Button
            className="flex-3 text-sm"
            onClick={handleNext}
            // 가입/지역 저장 처리 중에는 비활성화
            disabled={confirmOTPMutation.isPending}
            variant={
              currentTab === 1
                ? "black"
                : confirmOTPMutation.isPending
                  ? "disabled"
                  : "primary"
            }
            label={
              currentTab === 2
                ? "가입하기"
                : currentTab === 3
                  ? "보이스파크 시작하기"
                  : "다음"
            }
          />
        </div>
      </section>
    </div>
  );
}
